"use client";

import React, { useEffect, useRef, useState } from "react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { MapPin, Truck, Warehouse, ShieldCheck } from "lucide-react";
const iconMap = {
  warehouse: Warehouse,
  shield: ShieldCheck,
  truck: Truck,
  map: MapPin
};
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const infrastructureData = [{
  id: 1,
  title: "ALINEACIÓN Y CONTROL DE FRICCIÓN Y TEMPERATURA",
  description: "\n",
  image: "https://mivgrupo.com/wp-content/uploads/2026/04/Alineacion-tiny.jpg?v=yt41r4fhsc",
  specs: "TOLERANCIA: +/-0.005mm | CAPACIDAD: 5 EJES",
  iconKey: "warehouse"
}, {
  id: 2,
  title: "METROLOGÍA CERTIFICADA GARANTÍA DE FIABILIDAD OPERATIVA",
  description: "\n",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1632165/img/miv_precision_metrology_lab.png",
  specs: "CERTIFICACIÓN: ISO 9001 | PRECISIÓN: MICRÓNICA",
  iconKey: "shield"
}, {
  id: 3,
  title: "INGENIERÍA DE ENGRANAJES:\xA0 \xA0 \xA0 \xA0POTENCIA Y RENDIMIENTO GARANTIZADO",
  description: "\n",
  image: "https://mivgrupo.com/wp-content/uploads/2026/04/heavy_duty_gear_fabrication.webp?v=uo5xa7ginbn",
  specs: "MÓDULO: HASTA 30 | DIÁMETRO: 4000mm",
  iconKey: "truck"
}, {
  id: 4,
  title: "\n",
  description: "\n",
  image: "https://mivgrupo.com/wp-content/uploads/2026/04/mosaicoTrabajoEnSitio_Tiny.jpg?v=o5hndqlgoe",
  specs: "ALCANCE: NACIONAL | RESPUESTA: 24/7",
  iconKey: "map",
  idClassName: "min-w-0 shrink-0 grow-0 pl-2 md:pl-2 basis-full md:basis-1/2 lg:basis-1/2 !bg-[url(https://mivgrupo.com/wp-content/uploads/2026/04/Screenshot_20260423_180108_Gallery.jpg)]"
}];
export default function InfraestructuraYFlota() {
  const headerReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const carouselReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="136104" id="sectores" className="dark bg-background text-foreground py-24 overflow-hidden relative">
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none bg-[radial-gradient(circle_at_center,_var(--color-primary)_1px,_transparent_1px)] [background-size:32px_32px]" />

      <div id="servicios-industriales" className="container mx-auto px-4 md:px-6 relative z-10 scroll-mt-20">
        <div ref={headerReveal.ref} className={`max-w-3xl mb-16 transition-all duration-1000 ${headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="font-serif font-bold tracking-tight mb-6 text-foreground uppercase !text-2xl">MAXIMICE SU EFICIENCIA CON MECANIZADO, FABRICACIÓN&nbsp; Y REPARACIÓN DE PRECISIÓN</h2>
          <p className="text-lg text-muted-foreground leading-relaxed font-default">En MIV, fusionamos ingeniería de precisión con tecnología de vanguardia. Nuestro taller industrial está equipado para enfrentar los desafíos más exigentes de la industria pesada, garantizando exactitud técnica y durabilidad en cada componente mecanizado.</p>
        </div>

        <div ref={carouselReveal.ref} className={`transition-all duration-1000 delay-200 ${carouselReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <Carousel opts={{
          align: "start",
          loop: true
        }} className="w-full mx-auto">
            <CarouselContent className="-ml-4 md:-ml-6 flex flex-col gap-4 space-y-4 md:flex-row md:gap-0 md:space-y-0">
              {infrastructureData.map((item, i) => {
              const Icon = iconMap[item.iconKey as keyof typeof iconMap];
              return <CarouselItem key={item.id} data-index={i} className="min-w-0 shrink-0 grow-0 pl-2 basis-full w-full h-auto md:w-[700px] md:h-[800px] md:pl-2 md:basis-1/2 lg:basis-1/2 flex justify-center">
                    <div className="relative overflow-hidden rounded-sm group bg-muted border border-border/50 shadow-2xl w-full h-auto aspect-[4/3] md:aspect-[21/9] md:w-[800px] md:h-[550px]">
                      <img src={item.image} alt={item.title} className={`absolute inset-0 transition-transform duration-1000 group-hover:scale-110 ![margin:0px_2px_0px_2px] w-full h-auto object-contain md:w-[850px] md:h-[550px] ${i === 3 ? "md:object-contain md:bg-muted" : "md:object-cover"}`} />
                      <div className="absolute inset-0 bg-primary/10 mix-blend-overlay pointer-events-none z-10" />
                      
                      <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent z-20 group-hover:opacity-0 transition-opacity duration-500 !w-full md:!w-[750px] !h-full md:!h-[550px] !opacity-[0.01]" />

                      <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6 md:p-10 z-30 transition-all duration-500 md:group-hover:translate-y-8 group-hover:opacity-0 bg-background/40 md:bg-transparent md:inset-auto md:bottom-0 md:left-0 md:right-0 md:block md:text-left">
                        <div className={`flex flex-col md:flex-row items-center gap-4 md:gap-3 mb-4 md:mb-3 ${i === 3 ? "md:!justify-start" : "md:!justify-center"}`}>
                        
                          <h3 className="font-serif uppercase tracking-tight !font-extrabold leading-tight !text-sm !text-[hsl(189,94%,43%)] md:leading-normal">
                            {item.title}
                          </h3>
                        </div>
                        <p className="max-w-xl font-default !font-extrabold text-xl md:!text-2xl !text-[hsl(38,92%,50%)] mx-auto !opacity-[0.9] leading-relaxed">
                          {item.description}
                        </p>
                      </div>

                      <div className="absolute inset-0 bg-background/95 backdrop-blur-sm z-40 opacity-0 group-hover:opacity-100 transition-all duration-500 flex flex-col items-center justify-center p-8 text-center">
                        <div className="transform translate-y-8 group-hover:translate-y-0 transition-transform duration-500 delay-100">
                          <Icon className="w-12 h-12 text-primary mx-auto mb-6 opacity-90" />
                          <h4 className="text-2xl font-serif font-bold text-foreground mb-8 uppercase tracking-widest">
                            {item.title}
                          </h4>
                          
                          <div className="inline-block border border-chart-4/30 bg-chart-4/5 px-6 py-4 rounded-sm shadow-[0_0_40px_rgba(var(--color-chart-4),0.1)]">
                            <p className="font-mono text-chart-4 text-sm md:text-lg tracking-widest font-semibold">
                              {item.specs}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CarouselItem>;
            })}
            </CarouselContent>
            
            <div className="absolute bottom-[15%] left-0 right-0 z-50 flex items-center justify-center gap-8 pointer-events-none max-md:hidden">
              <CarouselPrevious className="static translate-y-0 bg-background/80 backdrop-blur-sm border-border hover:bg-primary hover:text-primary-foreground size-14 rounded-sm pointer-events-auto transition-all" />
              <CarouselNext className="static translate-y-0 bg-background/80 backdrop-blur-sm border-border hover:bg-primary hover:text-primary-foreground size-14 rounded-sm pointer-events-auto transition-all" />
            </div>
          </Carousel>
        </div>
      </div>
    </section>;
}